import { baseApiService } from './base'

export interface NotificationSetting {
  notificationType: 'NOTIFICATION' | 'EMAIL'
  enabled: boolean
  readOnly?: boolean
}

export interface NotificationSettingsItem {
  messageType: string
  setting: NotificationSetting[]
}

export interface NotificationSettingsResponse {
  availableSettings: NotificationSettingsItem[]
}

export interface ChangeNotificationSetting {
  messageType: string
  newState: boolean
  type: 'NOTIFICATION' | 'EMAIL'
}

export interface ChangeNotificationSettingsRequest {
  settings: ChangeNotificationSetting[]
}

export interface NotificationCountResponse {
  count: number
}

// UI format for my-settings page
export interface NotificationSettingUI {
  messageType: string
  value: string
  email: {
    enabled: boolean
    show: boolean
    readOnly: boolean
  }
  notification: {
    enabled: boolean
    show: boolean
    readOnly: boolean
  }
}

// Notification type translations mapping
const notificationTypes: Record<string, string> = {
  TASKS: 'NOTIFICATION_TYPE_TASKS',
  CHANGE_REQUEST: 'NOTIFICATION_TYPE_CHANGE_REQUEST',
  UPLOAD_FILES: 'NOTIFICATION_TYPE_UPLOAD_FILES',
  DOC_MIGRATIONS_EXPORT: 'NOTIFICATION_TYPE_DOC_MIGRATIONS_EXPORT',
  FOLDER_STRUCTURE: 'NOTIFICATION_TYPE_FOLDER_STRUCTURE',
  REPORT_EXPORT: 'NOTIFICATION_TYPE_REPORT_EXPORT',
  USER_ACCOUNTS_EXPIRATION: 'NOTIFICATION_TYPE_USER_ACCOUNTS_EXPIRATION',
  PERIODIC_REVIEW: 'NOTIFICATION_TYPE_PERIODIC_REVIEW',
  SETTINGS_SYSTEM_EVENTS: 'NOTIFICATION_TYPE_SETTINGS_SYSTEM_EVENTS',
  DEVICE_VERIFICATION: 'NOTIFICATION_TYPE_DEVICE_VERIFICATION',
  USER_ACCOUNT_LOCKED: 'NOTIFICATION_TYPE_USER_ACCOUNT_LOCKED',
  DOCUMENT_EXPIRATION: 'NOTIFICATION_TYPE_DOCUMENT_EXPIRATION',
  ETMF_EVENT: 'NOTIFICATION_TYPE_ETMF_EVENT',
  EOS: 'NOTIFICATION_TYPE_EOS',
  DOCUMENTS_REJECTION: 'NOTIFICATION_TYPE_DOC_REJECTION'
}

// Transform API response to UI format (for my-settings page)
export function processNotificationSettingsForUI(
  response: NotificationSettingsResponse,
  translateFn: (key: string) => string
): NotificationSettingUI[] {
  const returningSettings: NotificationSettingUI[] = []

  response.availableSettings.forEach((item) => {
    // Only include items that have a translation key
    if (notificationTypes[item.messageType]) {
      const emailSetting = item.setting.find((s) => s.notificationType === 'EMAIL')
      const notifSetting = item.setting.find((s) => s.notificationType === 'NOTIFICATION')

      const tmpObj: NotificationSettingUI = {
        messageType: item.messageType,
        value: translateFn(notificationTypes[item.messageType]),
        notification: { show: false, enabled: false, readOnly: false },
        email: { show: false, enabled: false, readOnly: false }
      }

      if (notifSetting) {
        tmpObj.notification.show = true
        tmpObj.notification.enabled = notifSetting.enabled
        tmpObj.notification.readOnly = notifSetting.readOnly || false
      }

      if (emailSetting) {
        tmpObj.email.show = true
        tmpObj.email.enabled = emailSetting.enabled
        tmpObj.email.readOnly = emailSetting.readOnly || false
      }

      returningSettings.push(tmpObj)
    }
  })

  return returningSettings.sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()))
}

// Transform UI format back to API format for saving
export function transformNotificationSettingsForAPI(
  uiSettings: NotificationSettingUI[]
): ChangeNotificationSettingsRequest {
  const settings: ChangeNotificationSetting[] = []

  uiSettings.forEach((item) => {
    if (item.notification.show) {
      settings.push({
        messageType: item.messageType,
        newState: item.notification.enabled,
        type: 'NOTIFICATION'
      })
    }
    if (item.email.show) {
      settings.push({
        messageType: item.messageType,
        newState: item.email.enabled,
        type: 'EMAIL'
      })
    }
  })

  return { settings }
}

export async function getNotificationSettings(): Promise<NotificationSettingsResponse> {
  return baseApiService.fetch<NotificationSettingsResponse>('/api/websocket/settings')
}

export async function changeNotificationSettings(data: ChangeNotificationSettingsRequest): Promise<void> {
  await baseApiService.fetch<void>('/api/websocket/changeNotificationSettings', {
    method: 'POST',
    body: { ...data }
  })
}

// FSI (File/Folder) Notification Types
export interface FsiNotificationResponse {
  systemGroupIds: number[]
  systemUserIds: number[]
  userGroupIds: number[]
  userUserIds: number[]
}

export interface FsiNotificationSaveRequest {
  groupIds: number[]
  userIds: number[]
  applyRecursively: boolean
  applyOnlyChanges: boolean
}

export async function getNotificationForFsi(fsiId: number): Promise<FsiNotificationResponse> {
  return baseApiService.fetch<FsiNotificationResponse>(`/api/notification/${fsiId}`)
}

export async function saveNotificationForFsi(fsiId: number, data: FsiNotificationSaveRequest): Promise<void> {
  await baseApiService.fetch<void>(`/api/notification/${fsiId}`, {
    method: 'PATCH',
    body: { ...data }
  })
}
